import { Injectable } from '@angular/core';
import { UserInfo } from '@delab/ngx-gen-ts-ixserv/models';
import {
    ArticleCategory,
    DelabNgxShopServiceDefinition,
    ShopOrder,
    ShopOrderDetails,
    ShopProfile,
    ShopOrderStatus as UiStatus,
} from '@delab/ngx-ixconnect-components';
import { BehaviorSubject, filter, map, Observable, switchMap, tap } from 'rxjs';
import { ArticleCategoryWithIdDto } from '../../../../.api-client/models/article-category-with-id-dto';
import { ArticleDto } from '../../../../.api-client/models/article-dto';
import { ArticleWithIdDto } from '../../../../.api-client/models/article-with-id-dto';
import { AuthenticationDto } from '../../../../.api-client/models/authentication-dto';
import { HeaderDto } from '../../../../.api-client/models/header-dto';
import { ProfileDto } from '../../../../.api-client/models/profile-dto';
import { ShopOrderDto } from '../../../../.api-client/models/shop-order-dto';
import { ShopOrderStatus as ApiStatus } from '../../../../.api-client/models/shop-order-status';
import { UserInfoDto } from '../../../../.api-client/models/user-info-dto';
import { ShopApiService } from '../../../../.api-client/services/shop-api.service';
import { AuthService } from '../../../services/auth.service';

const COUNTRY_CODE_DEFAULT = 'DE';

export function mapApiStatusToUi(apiStatus: ApiStatus): UiStatus {
    switch (apiStatus) {
        case 'in Bearbeitung':
            return UiStatus.OPEN;

        case 'versendet':
            return UiStatus.SHIPPED;

        default:
            throw new Error(`Unknown API status: ${apiStatus}`);
    }
}

@Injectable({
    providedIn: 'root',
})
export class ShopService implements DelabNgxShopServiceDefinition {
    private readonly _userInfoSource = new BehaviorSubject<UserInfo>(new UserInfo());
    public readonly userInfoObs = this._userInfoSource.asObservable();

    constructor(
        private readonly _shopApiService: ShopApiService,
        private readonly _authService: AuthService,
    ) {
        this._authService.authObs
            .pipe(
                filter(Boolean),
                map((auth: AuthenticationDto) => {
                    if (!auth.user.usrID) {
                        return;
                    }
                }),
            )
            .subscribe();

        this._authService.headerObs.pipe().subscribe({
            next: (headers: HeaderDto[] | undefined) => {
                if (!headers) {
                    return;
                }

                const agnExtId = headers.find((h) => h.key === 'x-agn-extid')?.value;
                const usrId = headers.find((h) => h.key === 'x-usr-id')?.value;

                const userInfo = new UserInfo();
                userInfo.agnExtID = agnExtId;
                userInfo.usrID = usrId ? Number(usrId) : undefined;

                this._userInfoSource.next(userInfo);
            },
        });
    }

    getShopAdmin(): Observable<boolean> {
        return this._authService.adminObs;
    }
    getUserInfo(): Observable<UserInfo> {
        return this._authService.authObs.pipe(map((auth) => new UserInfo(auth?.user)));
    }

    updateShopOrder(shopOrder: ShopOrder): Observable<ShopOrder> {
        return this._shopApiService.shopUpdateOrder({ id: shopOrder.orderId, body: shopOrder }).pipe(
            map((order) => {
                const updatedOrder: ShopOrder = {
                    orderId: order.orderId,
                    orderDetails: order.orderDetails,
                    orderDate: order.orderDate,
                    userInfo: new UserInfo(order.userInfo),
                    orderStatus: mapApiStatusToUi(order.orderStatus),
                    xccExtId: order.xccExtId,
                    sendDate: order.sendDate,
                    note: order.note,
                    pdf: order.pdf,
                };

                return updatedOrder;
            }),
        );
    }

    public getProfile(userId: string): Observable<ProfileDto | null> {
        return this._shopApiService.shopGetShopProfile({ id: userId }).pipe(map((value) => value ?? null));
    }

    public getArticles(): Observable<ArticleWithIdDto[]> {
        return this._shopApiService.shopGetArticles().pipe(
            map((value) => {
                if (typeof value === 'undefined') {
                    throw Error();
                }
                return value;
            }),
        );
    }

    public getTotalNumber(unitNumber: any, vpeNumber: any): string {
        const total = unitNumber * vpeNumber;
        return total.toString();
    }

    public getArticleCategories(): Observable<ArticleCategoryWithIdDto[]> {
        return this._shopApiService.shopGetCategories().pipe(
            map((value) => {
                if (typeof value === 'undefined') {
                    throw Error();
                }
                return value;
            }),
        );
    }

    public getShopOrders(userInfo: UserInfo): Observable<ShopOrder[]> {
        return this._shopApiService.shopGetShopOrders({ body: userInfo }).pipe(
            map((shopOrders) => {
                if (typeof shopOrders === 'undefined') {
                    throw Error();
                }
                return shopOrders.map((order: ShopOrderDto) => ({
                    orderId: order.orderId,
                    orderDetails: order.orderDetails,
                    orderDate: order.orderDate,
                    userInfo: new UserInfo(order.userInfo),
                    orderStatus: mapApiStatusToUi(order.orderStatus),
                    xccExtId: order.xccExtId,
                    sendDate: order.sendDate,
                    note: order.note,
                    pdf: order.pdf,
                }));
            }),
        );
    }

    public createShopOrder(orderDetails: ShopOrderDetails[], userInfo: UserInfoDto): Observable<ShopOrder> {
        return this._shopApiService
            .shopCreateOrder({
                body: {
                    orderDetails: orderDetails,
                    userInfo: userInfo,
                },
            })
            .pipe(
                tap((shopOrder) => console.log('shopOrder-creation:', shopOrder)),
                map(
                    (shopOrder: ShopOrderDto): ShopOrder => ({
                        orderId: shopOrder.orderId,
                        orderDetails: shopOrder.orderDetails,
                        orderDate: shopOrder.orderDate,
                        userInfo: new UserInfo(userInfo),
                        orderStatus: mapApiStatusToUi(shopOrder.orderStatus),
                        xccExtId: shopOrder.xccExtId,
                    }),
                ),
            );
    }

    public updateOrder(shopOrder: ShopOrderDto): Observable<ShopOrderDto> {
        return this._shopApiService.shopUpdateOrder({ id: shopOrder.orderId, body: shopOrder });
    }

    public updateShopProfile(shopProfile: ShopProfile): Observable<ShopProfile> {
        return this._shopApiService.shopUpdateShopProfile({ body: shopProfile });
    }

    public updateArticle(article: ArticleWithIdDto): Observable<ArticleWithIdDto> {
        return this._shopApiService.shopUpdateArticle({ id: article._id, body: article });
    }

    public insertArticle(article: ArticleDto): Observable<ArticleWithIdDto> {
        return this._shopApiService.shopInsertArticle({ body: article });
    }

    public deleteArticle(article: ArticleWithIdDto): Observable<void> {
        return this._shopApiService.shopDeleteArticle({ id: article._id, body: article });
    }

    public insertArticleCategory(category: ArticleCategory): Observable<ArticleCategoryWithIdDto> {
        return this._shopApiService.shopInsertArticleCategory({ body: category }).pipe(tap(() => this.getArticleCategories()));
    }

    public updateArticleCategory(category: ArticleCategoryWithIdDto): Observable<ArticleCategoryWithIdDto> {
        return this._shopApiService
            .shopUpdateArticleCategory({ id: category._id, body: category })
            .pipe(tap(() => this.getArticleCategories()));
    }

    public deleteArticleCategory(category: ArticleCategoryWithIdDto): Observable<void> {
        return this._shopApiService.shopDeleteArticleCategory({ id: category._id, body: category }).pipe(
            switchMap((value) => this.getArticleCategories()),
            map((value) => {}),
        );
    }
}
